import {Amplify} from 'aws-amplify';

export const SSO_CLIENT_ID_KEY = 'sso_client_id';
export const AUTH_TYPE_KEY = 'auth_type';

// Amplify keys its token storage by client id, so the redirect and the OAuth
// callback must be configured with the same client id.
export const configureAmplify = (clientId?: string): void => {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: import.meta.env.VITE_COGNITO_USERPOOL_ID as string,
        userPoolClientId:
          clientId ?? (import.meta.env.VITE_COGNITO_CLIENT_ID as string),
        loginWith: {
          oauth: {
            domain: import.meta.env.VITE_COGNITO_DOMAIN as string,
            scopes: ['email', 'openid', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [window.location.origin],
            redirectSignOut: [window.location.origin],
            responseType: 'code',
          },
        },
      },
    },
  });
};

export const readSsoClientId = (): string | null => {
  try {
    const stored = window.localStorage.getItem(SSO_CLIENT_ID_KEY);
    return stored && stored.trim().length > 0 ? stored : null;
  } catch {
    return null;
  }
};

export const writeSsoClientId = (clientId?: string | null): void => {
  try {
    if (clientId && clientId.trim().length > 0) {
      window.localStorage.setItem(SSO_CLIENT_ID_KEY, clientId);
    } else {
      window.localStorage.removeItem(SSO_CLIENT_ID_KEY);
    }
  } catch {
    // Storage unavailable (private mode, disabled cookies): routing falls back
    // to the platform client.
  }
};

export const writeAuthType = (authType: number): void => {
  try {
    window.localStorage.setItem(AUTH_TYPE_KEY, String(authType));
  } catch {
    // Storage unavailable: the login step falls back to a JWT prompt.
  }
};

export const clearLoginRouting = (): void => {
  try {
    window.localStorage.removeItem(AUTH_TYPE_KEY);
    window.localStorage.removeItem(SSO_CLIENT_ID_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
};

// The SAML values a client pastes into their IdP are derived from the same pool
// and hosted-UI domain Amplify is configured with.
export const formatSamlEntityId = (): string =>
  `urn:amazon:cognito:sp:${import.meta.env.VITE_COGNITO_USERPOOL_ID as string}`;

export const formatSamlAcsUrl = (): string =>
  `https://${import.meta.env.VITE_COGNITO_DOMAIN as string}/saml2/idpresponse`;

// Named without the `use` prefix so it is not treated as a React hook: the SSO
// runtime clients allow only refresh, so platform entry pages must reset first.
export const resetToPlatformClient = (): void => {
  writeSsoClientId(null);
  configureAmplify();
};
